import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeLink, setActiveLink] = useState('');
    const [hoveredLink, setHoveredLink] = useState(null);
    const navRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            setScrolled(scrollY > 20);
            
            // Update active link based on scroll position
            const sections = document.querySelectorAll('section');
            let currentSection = '';
            
            sections.forEach((section) => {
                const sectionTop = section.offsetTop - 100;
                const sectionHeight = section.offsetHeight;
                if (scrollY >= sectionTop && scrollY < sectionTop + sectionHeight) {
                    currentSection = `#${section.id}`;
                }
            });
            
            setActiveLink(currentSection);
            // Close mobile menu on scroll
            setIsMenuOpen(false);
        };
        
        // Initial check on mount
        handleScroll();
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { href: '#about', text: 'About' },
        { href: '#skills', text: 'Skills' },
        { href: '#projects', text: 'Projects' },
        { href: '#certifications', text: 'Certifications' },
        { href: '#resume', text: 'Resume' },
        { href: '#contact', text: 'Contact' }
    ];

    // Animation variants
    const menuVariants = {
        hidden: { 
            opacity: 0,
            height: 0,
            transition: { 
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1],
                when: "afterChildren"
            }
        },
        visible: { 
            opacity: 1,
            height: "auto",
            transition: { 
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1],
                staggerChildren: 0.05
            }
        }
    };

    const itemVariants = {
        hidden: { 
            y: -10, 
            opacity: 0,
            transition: {
                duration: 0.2
            }
        },
        visible: { 
            y: 0, 
            opacity: 1,
            transition: {
                duration: 0.3,
                ease: "easeOut"
            }
        }
    };

    const logoVariants = {
        initial: { 
            opacity: 0, 
            x: -10
        },
        animate: { 
            opacity: 1, 
            x: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        },
        hover: {
            scale: 1.03,
            transition: {
                duration: 0.2,
                ease: "easeInOut"
            }
        }
    };

    const linkHoverVariants = {
        initial: {
            scale: 1,
            color: "#374151"
        },
        hover: {
            scale: 1.05,
            color: "#6366f1",
            transition: {
                duration: 0.2,
                ease: "easeOut"
            }
        }
    };

    const underlineVariants = {
        initial: { width: 0, left: "50%" },
        hover: { width: "80%", left: "10%" }
    };

    return (
        <nav 
            ref={navRef}
            className={`fixed w-full z-50 transition-all duration-500 ${
                scrolled 
                    ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-sm py-0' 
                    : 'bg-transparent py-2'
            } ${isMenuOpen ? 'bg-white dark:bg-gray-900 shadow-lg' : ''}`}
        >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    <motion.div 
                        className="flex-shrink-0"
                        variants={logoVariants}
                        initial="initial"
                        animate="animate"
                        whileHover="hover"
                    >
                        <motion.h3 
                            className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent p-2 rounded-lg"
                            whileHover={{
                                backgroundImage: "linear-gradient(to right, #ec4899, #8b5cf6)",
                                transition: { duration: 0.3 }
                            }}
                        >
                            Thogiti Veerendra
                        </motion.h3>
                    </motion.div>
                    
                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-1">
                        {navLinks.map((link) => (
                            <motion.div
                                key={link.href}
                                className="relative px-1"
                                onHoverStart={() => setHoveredLink(link.href)}
                                onHoverEnd={() => setHoveredLink(null)}
                            >
                                <motion.a
                                    href={link.href}
                                    className={`relative px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300 ${
                                        activeLink === link.href 
                                            ? 'text-white'
                                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                                    variants={linkHoverVariants}
                                    initial="initial"
                                    whileHover="hover"
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {link.text}
                                    {activeLink === link.href && (
                                        <motion.div 
                                            className="absolute inset-0 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 z-[-1]"
                                            layoutId="activeNavLink"
                                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                        />
                                    )}
                                </motion.a>
                                
                                {/* Hover underline effect */}
                                {hoveredLink === link.href && activeLink !== link.href && (
                                    <motion.div
                                        className="absolute bottom-0 h-0.5 bg-gradient-to-r from-purple-400 to-indigo-400 rounded-full"
                                        variants={underlineVariants}
                                        initial="initial"
                                        animate="hover"
                                        transition={{ duration: 0.2 }}
                                    />
                                )}
                            </motion.div>
                        ))}
                    </div>

                    {/* Mobile menu button */}
                    <motion.div 
                        className="flex md:hidden"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500 transition-all duration-200"
                            aria-expanded={isMenuOpen}
                        >
                            <span className="sr-only">Open main menu</span>
                            <motion.div
                                animate={isMenuOpen ? "open" : "closed"}
                                variants={{
                                    closed: { rotate: 0, opacity: 1 },
                                    open: { rotate: 90, opacity: 0.8 }
                                }}
                                transition={{ duration: 0.2 }}
                            >
                                {!isMenuOpen ? (
                                    <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                ) : (
                                    <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                )}
                            </motion.div>
                        </button>
                    </motion.div>
                </div>

                {/* Mobile menu with animations */}
                <AnimatePresence mode="wait">
                    {isMenuOpen && (
                        <motion.div 
                            className="md:hidden overflow-hidden"
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            variants={menuVariants}
                        >
                            <motion.div 
                                className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white dark:bg-gray-900 rounded-lg mt-2 shadow-lg border border-gray-100 dark:border-gray-800"
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: { 
                                        opacity: 1,
                                        transition: {
                                            delay: 0.1,
                                            staggerChildren: 0.05
                                        }
                                    }
                                }}
                            >
                                {navLinks.map((link) => (
                                    <motion.div
                                        key={link.href}
                                        className="relative"
                                        variants={itemVariants}
                                    >
                                        <motion.a
                                            href={link.href}
                                            className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${
                                                activeLink === link.href
                                                    ? 'text-white bg-gradient-to-r from-indigo-600 to-purple-600'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                            }`}
                                            onClick={() => setIsMenuOpen(false)}
                                            whileHover={{ 
                                                x: 5,
                                                transition: { duration: 0.15 }
                                            }}
                                        >
                                            {link.text}
                                        </motion.a>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </nav>
    );
}